// `station.app.install` — "Install" on one PC, or "Install on PCs missing it" (F-CAT-09, ADR-0056; spec/10 §7.5.2,
// spec/09 §6.2, spec/13 §3.3, §9.1).
//
// A library app with a provisioning recipe (`game.provisioning`) installs into the account players use when the venue
// turns Install for players on — at idle moments, two minutes after the lock screen, with backoff after a failure. Staff
// may now ask for ONE app on one PC (`POST …/stations/{s}/apps/{entry}/install`) or on every PC of the venue missing it
// (`POST …/library/{entry}/install`); the api sends this command to each PC, and the station
//
//   - checks the entry is in its signed library with a recipe it can act on, and that it is a PC that installs apps (never
//     a diskless client, Clavian Counter, or with the lockdown off) — else it answers `no_recipe` / `not_installable`;
//   - answers `installed` when it already knows the app is in the player account (nothing to do);
//   - otherwise keeps the request (`app-install-requests.json`, so a restart keeps it) and answers `queued`. It installs
//     the app as soon as the PC is free: `locked`, at once — no two-minute settle and no backoff for that app — but never
//     in a session, maintenance, an update, disabled or pairing; while the PC is `setting_up` the start-up setup's own
//     apps go first. A request made while the PC is busy or offline waits for the next free moment. It installs exactly
//     as Install for players does (the allowed hosts, the pinned signer, the agent running the installer as the player,
//     the snapshot), into the account players use, and it does NOT turn the entry's Install for players switch on.
//
// The request is cleared once the app is installed, or once an attempt fails (the failure is reported, and "Retry" asks
// again); an attempt stopped by a session is not a failure, and goes again at the next free moment. A request not met
// within seven days of `requestedAt` expires (the command expires on the same clock, so an offline PC that comes back
// later is not sent it). While it waits, the station reports the app `queued` in its install report.
//
// It answers within the ack window (spec/09 §5.1) with `StationAppInstallCommandResult`. A station built before this
// command answers `ok: false` `unsupported`: the console says that PC needs a newer station app.
import { z } from "../zod.js";
import { LibraryEntryIdSchema, MemberIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { StationModeSchema } from "./station.js";
/** The command type (spec/09 §6.2). Its default expiry is seven days (`STATION_COMMAND_EXPIRY_SECONDS`). */
export const STATION_APP_INSTALL = 'station.app.install';
/** How long a request waits for its PC to be free before it lapses: the command's expiry, and the station's own. */
export const APP_INSTALL_REQUEST_DAYS = 7;
export const StationAppInstallCommandPayloadSchema = z
    .object({
    /** The library entry to install (an app of the PC's signed library with a provisioning recipe). */
    entryId: LibraryEntryIdSchema,
    /** The member who asked (the audit row names them too). */
    by: MemberIdSchema,
    /** When staff asked, RFC 3339 UTC: the request lapses seven days after it. */
    requestedAt: IsoDateTimeSchema,
})
    .openapi('StationAppInstallCommandPayload', {
    description: '`station.app.install`: install this library app into the account players use as soon as the PC is free, never during a ' +
        'session; one-off, without turning Install for players on (F-CAT-09, ADR-0056).',
});
/**
 * What the station did with the command, as its ack's `result` says:
 *
 *   queued           it keeps the request and installs the app as soon as the PC is free (at once if it is free now)
 *   installed        the app is already in the account players use: nothing to do
 *   no_recipe        the entry is not in this PC's library, or carries no recipe this station can act on
 *   not_installable  this PC never installs apps for players (a diskless client: install it on the image)
 */
export const STATION_APP_INSTALL_OUTCOMES = ['queued', 'installed', 'no_recipe', 'not_installable'];
export const StationAppInstallCommandResultSchema = z
    .object({
    outcome: z.enum(STATION_APP_INSTALL_OUTCOMES),
    /** The station's mode when it answered: why a `queued` install waits (a session, maintenance…). */
    mode: StationModeSchema,
})
    .openapi('StationAppInstallCommandResult', {
    description: 'What a station did with `station.app.install`: queued until the PC is free, already installed, or why it cannot (ADR-0056).',
});
//# sourceMappingURL=app-install.js.map